/**
 * Analytics Dashboard Component - Super Admin
 * Displays aggregate metrics across all franchises
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, TrendingUp, DollarSign, Users, FileText, Building2,
  QrCode, Sparkles, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import { authAxios } from "@/utils/auth";

function StatCard({ title, value, subtext, icon: Icon, trend, trendUp }) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subtext && (
              <div className="flex items-center gap-1 mt-1">
                {trend !== undefined && (
                  trendUp ? (
                    <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 text-red-500" />
                  )
                )}
                <p className={`text-xs ${trendUp ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {subtext}
                </p>
              </div>
            )}
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <Icon className="w-6 h-6 text-blue-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ServiceRow({ service, maxRevenue }) {
  const percentage = maxRevenue > 0 ? (service.total_revenue / maxRevenue) * 100 : 0;
  
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-slate-800">{service.service_name}</span>
        <span className="text-sm text-slate-600">{service.quote_count} quotes</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-medium text-emerald-600 w-20 text-right">
          ${service.total_revenue.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function FranchiseRow({ franchise }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${franchise.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
          <div>
            <p className="font-medium text-slate-800">{franchise.name}</p>
            <p className="text-xs text-slate-500">/{franchise.slug}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-medium text-emerald-600">${franchise.revenue.toLocaleString()}</p>
          <p className="text-xs text-slate-500">{franchise.quotes} quotes</p>
        </div>
      </div>
    </div>
  );
}

function CampaignRow({ campaign }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800">{campaign.name}</span>
            {campaign.is_active ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
            ) : (
              <Badge variant="outline" className="text-xs">Inactive</Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">{campaign.neighborhood || campaign.code}</p>
        </div>
        <div className="text-right">
          <p className="font-medium text-emerald-600">${campaign.revenue.toLocaleString()}</p>
          <p className="text-xs text-slate-500">{campaign.scans} scans • {campaign.conversion_rate}% conv</p>
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState(null);
  const [services, setServices] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchAnalytics = useCallback(async () => {
    try {
      const [overviewRes, servicesRes, franchisesRes, campaignsRes] = await Promise.all([
        authAxios.get("/analytics/overview"),
        authAxios.get("/analytics/services/popularity"),
        authAxios.get("/analytics/franchises/performance"),
        authAxios.get("/analytics/campaigns/performance")
      ]);
      
      setOverview(overviewRes.data);
      setServices(servicesRes.data.services || []);
      setFranchises(franchisesRes.data.franchises || []);
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (error) {
      toast.error("Failed to load analytics");
      console.warn("Analytics fetch error:", error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div data-testid="analytics-dashboard">
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        </div>
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const maxServiceRevenue = Math.max(...services.map(s => s.total_revenue), 1);

  return (
    <div data-testid="analytics-dashboard">
      <div className="flex items-center gap-3 mb-8">
        <BarChart3 className="w-8 h-8 text-blue-600" />
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
      </div>

      {/* Overview Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Revenue"
          value={`$${overview?.revenue?.total?.toLocaleString() || 0}`}
          subtext={`$${overview?.revenue?.this_month?.toLocaleString() || 0} this month`}
          icon={DollarSign}
          trend={10}
          trendUp={true}
        />
        <StatCard
          title="Total Quotes"
          value={overview?.quotes?.total?.toLocaleString() || 0}
          subtext={`${overview?.quotes?.this_month || 0} this month`}
          icon={FileText}
          trend={5}
          trendUp={true}
        />
        <StatCard
          title="Lead Conversion"
          value={`${overview?.leads?.conversion_rate || 0}%`}
          subtext={`${overview?.leads?.converted || 0} of ${overview?.leads?.total || 0} leads`}
          icon={TrendingUp}
        />
        <StatCard
          title="Active Franchises"
          value={overview?.franchises?.active || 0}
          subtext={`of ${overview?.franchises?.total || 0} total`}
          icon={Building2}
        />
      </div>

      {/* Subscription Revenue */}
      {overview?.revenue?.subscription > 0 && (
        <Card className="mb-8 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-indigo-600" />
                <div>
                  <p className="font-medium text-indigo-900">Subscription Revenue</p>
                  <p className="text-sm text-indigo-600">From franchise monthly subscriptions</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-indigo-700">
                ${overview.revenue.subscription.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for detailed views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview">Services</TabsTrigger>
          <TabsTrigger value="franchises">Franchises</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Popular Services
                </CardTitle>
              </CardHeader>
              <CardContent>
                {services.length > 0 ? (
                  services.slice(0, 8).map((service) => (
                    <ServiceRow 
                      key={service.service_id} 
                      service={service}
                      maxRevenue={maxServiceRevenue}
                    />
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-8">No service data yet</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Quotes Today</span>
                    <Badge className="bg-blue-100 text-blue-800">{overview?.quotes?.today || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Quotes This Week</span>
                    <Badge className="bg-emerald-100 text-emerald-800">{overview?.quotes?.this_week || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Quotes This Month</span>
                    <Badge className="bg-purple-100 text-purple-800">{overview?.quotes?.this_month || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-slate-600">Total Leads</span>
                    <Badge variant="outline">{overview?.leads?.total || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="franchises">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                Franchise Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {franchises.length > 0 ? (
                franchises.map((franchise) => (
                  <FranchiseRow key={franchise.franchise_id} franchise={franchise} />
                ))
              ) : (
                <p className="text-slate-500 text-center py-8">No franchise data yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <QrCode className="w-5 h-5 text-purple-500" />
                Campaign Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <CampaignRow key={campaign.code} campaign={campaign} />
                ))
              ) : (
                <p className="text-slate-500 text-center py-8">No campaign data yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
